import axios from 'axios';
import logger from '../utils/logger';

const API_BASE_URL = process.env.REACT_APP_ORTHOMIND_API_URL || 'https://api.orthomind.ai';

// Create a dedicated auth API instance
const authApi = axios.create({
  baseURL: API_BASE_URL,
  timeout: 30000,
  headers: {
    'Content-Type': 'application/json',
  },
});

interface SignInResponse {
  user_id: string;
  email: string;
  name?: string;
  role?: string;
  access_token: string;
  refresh_token: string;
  expires_in: number;
  email_verified?: boolean;
}

interface SignUpResponse {
  user_id?: string;
  email?: string;
  access_token?: string;
  refresh_token?: string;
  expires_in?: number;
  message?: string;
  requires_email_verification?: boolean;
}

interface UserResponse {
  user_id: string;
  email: string;
  name: string;
  role: string;
  email_verified: boolean;
  created_at: string;
}

export const authService = {
  // Sign in with email and password
  async signIn(email: string, password: string): Promise<SignInResponse> {
    try {
      const response = await authApi.post('/auth/signin', {
        email,
        password,
      });
      return response.data;
    } catch (error: any) {
      logger.error('Sign in error:', error);
      throw error;
    }
  },

  // Sign up with email, password, and name
  async signUp(email: string, password: string, name: string, role: string = 'orthodontist'): Promise<SignUpResponse> {
    try {
      const response = await authApi.post('/auth/signup', {
        email,
        password,
        name,
        role,
      });
      return response.data;
    } catch (error: any) {
      logger.error('Sign up error:', error);
      throw error;
    }
  },

  // Sign out
  async signOut(access_token: string): Promise<void> {
    try {
      await authApi.post('/auth/signout', {
        access_token,
      });
    } catch (error: any) {
      logger.error('Sign out error:', error);
      // Don't throw - we want to sign out locally even if backend fails
    }
  },

  // Get current user info
  async getCurrentUser(access_token: string): Promise<UserResponse> {
    try {
      const response = await authApi.get('/auth/me', {
        params: { access_token },
      });
      return response.data;
    } catch (error: any) {
      logger.error('Get current user error:', error);
      throw error;
    }
  },

  // Refresh access token
  async refreshToken(refresh_token: string): Promise<SignInResponse> {
    try {
      const response = await authApi.post('/auth/refresh', {
        refresh_token,
      });
      return response.data;
    } catch (error: any) {
      logger.error('Refresh token error:', error);
      throw error;
    }
  },

  // Reset password
  async resetPassword(email: string): Promise<void> {
    try {
      await authApi.post('/auth/reset-password', {
        email,
      });
    } catch (error: any) {
      logger.error('Reset password error:', error);
      throw error;
    }
  },

  // Update password
  async updatePassword(access_token: string, new_password: string): Promise<void> {
    try {
      await authApi.post('/auth/update-password', {
        access_token,
        new_password,
      });
    } catch (error: any) {
      logger.error('Update password error:', error);
      throw error;
    }
  },
};

export default authService;